/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RaiseTicket.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import CommonBreadcrumbs from "../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import ProjectVariantDialog from "../../../components/customComponentsV2/vlc/ProjectVarientAccordian/ProjectVariantDialog";
import ProjectVariantSearchbar from "../../../components/customComponentsV2/common/projectSearchbar/ProjectvariantSearchbar";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RaiseEETicket from "./RaiseEETicket";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import { HandleApiActions } from "../../../redux/actions";
import RaiseSoftwareTickets from "./RaiseSoftwareTickets";
import RaiseVehicleTickets from "./RaiseVehicleTickets";
import CustomTabsV2 from "../../../components/customComponentsV2/common/tabs/CustomTabsV2";
import smartMatchActions from "../../../redux/actions/smart-matching/smartMatchSuggestionActions";
import { toast } from "react-toastify";
import testCasesActions from "../../../redux/actions/testCasesActions";

const TICKET_COMPONENT_BY_TAB = {
  0: "vehicle-system",
  1: "vehicle-component",
  2: "ee",
  3: "swm",
};

function RaiseTicket() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  const currentLocation = useLocation();
  const urlParams = new URLSearchParams(currentLocation.search);
  const tabFromURL = urlParams.get("tab");
  const [savedEELinkages, setSavedEELinkages] = useState([]);
  const [savedSWMLinkages, setSavedSWMLinkages] = useState([]);
  const [savedVehicleSystemLinkages, setSavedVehicleSystemLinkages] = useState(
    [],
  );
  const [savedVehicleComponentLinkages, setSavedVehicleComponentLinkages] =
    useState([]);

  const [ticketComponent, setTicketComponent] = useState("vehicle-system");
  const [disableRaiseTicketButton, setDisableRaiseTicketButton] =
    useState(true);

  const [currentTabValue, setCurrentTabValue] = useState(0);

  const getTicketComponentByTab = (tabIndex) =>
    TICKET_COMPONENT_BY_TAB[Number(tabIndex)] || TICKET_COMPONENT_BY_TAB[0];

  const getTabState = (tabIndex) => {
    const tabNumber = Number(tabIndex);
    const linkagesByTab = {
      0: savedVehicleSystemLinkages,
      1: savedVehicleComponentLinkages,
      2: savedEELinkages,
      3: savedSWMLinkages,
    };

    return {
      component: getTicketComponentByTab(tabNumber),
      linkages: linkagesByTab[tabNumber] || savedVehicleSystemLinkages,
    };
  };

  // recreate state
  const [isLoading, setIsLoading] = useState(false);

  /* Smart matching states  */
  const [kgLoading, setKgLoading] = useState(false);

  const uniqueIds = (ids = []) =>
    Array.from(
      new Set(
        ids
          .map(Number)
          .filter((id) => Number.isFinite(id) && id > 0),
      ),
    );

  const getExistingRequirementIds = (testCase) =>
    uniqueIds(
      (testCase?.requirements || []).flatMap(
        (requirement) => requirement?.requirement_ids || [],
      ),
    );

  const getExistingComponentIds = (testCase, entityType) =>
    uniqueIds(
      (testCase?.component_to_link || [])
        .filter((component) => component?.entity_type === entityType)
        .flatMap((component) => component?.entity_ids || []),
    );

  const getLinkageComponentIds = (linkage, entityType) => {
    switch (entityType) {
      case "ee":
        return uniqueIds([linkage?.ee_id]);
      case "swm":
        return uniqueIds([linkage?.swm_id]);
      case "vs":
        return uniqueIds([linkage?.vs_id]);
      case "vc":
        return uniqueIds((linkage?.vc_to_legal || []).map((vc) => vc?.vc_id));
      default:
        return [];
    }
  };

  const getTestCaseUpdateRequests = ({ linkages, entityType }) => {
    const updateMap = new Map();

    linkages.forEach((linkage) => {
      const legalItems = Array.isArray(linkage?.legal)
        ? linkage.legal
        : [linkage];

      legalItems.forEach((legal) => {
        const selectedLrId = legal?.lr_id;
        const selectedComponentIds = getLinkageComponentIds(
          linkage,
          entityType,
        );

        (legal?.test_cases || []).forEach((testCase) => {
          if (
            testCase?.is_component_linked !== false &&
            testCase?.is_lr_linked !== false
          ) {
            return;
          }

          const testCaseId = testCase?.test_case_id;
          if (!testCaseId) {
            return;
          }

          const existingUpdate = updateMap.get(testCaseId) || {
            testCase,
            componentIds: getExistingComponentIds(testCase, entityType),
            requirementIds: getExistingRequirementIds(testCase),
            needsComponentUpdate: false,
            needsRequirementUpdate: false,
          };

          if (testCase?.is_component_linked === false) {
            existingUpdate.componentIds = uniqueIds([
              ...existingUpdate.componentIds,
              ...selectedComponentIds,
            ]);
            existingUpdate.needsComponentUpdate = true;
          }

          if (testCase?.is_lr_linked === false) {
            existingUpdate.requirementIds = uniqueIds([
              ...existingUpdate.requirementIds,
              selectedLrId,
            ]);
            existingUpdate.needsRequirementUpdate = true;
          }

          updateMap.set(testCaseId, existingUpdate);
        });
      });
    });

    return Array.from(updateMap.values()).map((update) => ({
      test_case_id: update.testCase?.test_case_id,
      description: update.testCase?.description || update.testCase?.name || "",
      status: update.testCase?.status || "in-progress",
      ...(update.needsComponentUpdate && {
        component_to_link: {
          entity_type: entityType,
          entity_ids: update.componentIds,
        },
      }),
      ...(update.needsRequirementUpdate && {
        requirement_ids: update.requirementIds,
      }),
    }));
  };

  const updateTestCaseLinksBeforeTicketCreation = async ({
    linkages,
    entityType,
  }) => {
    const updateRequests = getTestCaseUpdateRequests({ linkages, entityType });
    const updatedTestCaseIdMap = new Map();

    for (const payload of updateRequests) {
      const resp = await dispatch(
        HandleApiActions({
          ...testCasesActions.update_test_case,
          params: payload,
          show_toast: true,
        }),
      );

      if (resp.error) {
        return null;
      }

      const updatedTestCaseId = resp?.data?.test_case_id;
      if (updatedTestCaseId) {
        updatedTestCaseIdMap.set(
          String(payload.test_case_id),
          updatedTestCaseId,
        );
      }
    }

    return updatedTestCaseIdMap;
  };

  const replaceUpdatedTestCaseIds = (testCaseIds, updatedTestCaseIdMap) =>
    (testCaseIds || []).map((testCaseId) => {
      const updatedTestCaseId = updatedTestCaseIdMap?.get(String(testCaseId));
      return updatedTestCaseId || testCaseId;
    });

  const sanitizeLinkagesForTicketCreation = (
    updatedTestCaseIdMap,
    linkages = [],
  ) =>
    linkages.map((linkage) => {
      const { addOn, legal, test_cases, ...restLinkage } = linkage;
      const sanitizedLinkage = {
        ...restLinkage,
        ...(Array.isArray(restLinkage.test_case_ids)
          ? {
            test_case_ids: replaceUpdatedTestCaseIds(
              restLinkage.test_case_ids,
              updatedTestCaseIdMap,
            ),
          }
          : {}),
      };

      if (!Array.isArray(legal)) {
        return sanitizedLinkage;
      }

      return {
        ...sanitizedLinkage,
        legal: legal.map((legalItem) => {
          const { test_cases, ...restLegalItem } = legalItem;
          return {
            ...restLegalItem,
            ...(Array.isArray(restLegalItem.test_case_ids)
              ? {
                test_case_ids: replaceUpdatedTestCaseIds(
                  restLegalItem.test_case_ids,
                  updatedTestCaseIdMap,
                ),
              }
              : {}),
          };
        }),
      };
    });

  const handleKgBulkRefresh = async () => {
    if (kgLoading) return { skipped: true };
    setKgLoading(true);

    try {
      let resp = await dispatch(
        HandleApiActions({
          https_method: "POST",
          ...smartMatchActions?.refresh_kg_nodes,
          params: {},
          show_toast: false,
          ignore_app_loading_spinner: true,
        }),
      );
      toast.success(resp?.message);
      if (resp?.jobId) {
        const pollJobStatus = async () => {
          const streamResp = await dispatch(
            HandleApiActions({
              https_method: "GET",
              ...smartMatchActions?.get_live_stream_queue,
              api_name: `/smartMatch/api/v1/kg/queue/stream/${resp.jobId}`,
              params: {},
              show_toast: false,
              ignore_app_loading_spinner: true,
              // headers: {
              //   "X-User-Id": userProfile?.user_id,
              // },
            }),
          );

          let state = null;

          if (typeof streamResp === "string") {
            const lines = streamResp
              .split("\n")
              .filter((line) => line.trim());
            const lastLine = lines.at(-1);

            if (lastLine.startsWith("data:")) {
              try {
                const jsonStr = lastLine.replace(/^data:/, "").trim();
                const parsed = JSON.parse(jsonStr);
                state = parsed.state;
              } catch (e) {
                console.log("Error parsing JSON from stream response:", e);
              }
            }
          } else {
            state = streamResp?.state || streamResp?.data?.state;
          }

          if (state === "COMPLETED") {
            toast.success("Knowledge Graph Build Completed.");
          } else if (state === "RUNNING" || state === "QUEUED") {
            setTimeout(pollJobStatus, 2000);
          }
        };

        pollJobStatus();
      }
    } finally {
      setKgLoading(false);
    }
  };

  const handleLinkEEToLRs = async ({ draft }) => {
    const updatedTestCaseIdMap = await updateTestCaseLinksBeforeTicketCreation({
      linkages: savedEELinkages,
      entityType: "ee",
    });
    if (!updatedTestCaseIdMap) {
      return;
    }

    let apiDetails = typeApprovalAPI.add_ee_intersection;
    let intersectionDetails = sanitizeLinkagesForTicketCreation(
      updatedTestCaseIdMap,
      savedEELinkages,
    );
    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          variant_id: Number(selectedVariantDetails?.variant_id),
          intersections: intersectionDetails,
          is_save_draft: draft,
          current_time: new Date().toISOString(),
        },
        show_toast: true,
      }),
    );
    if (!resp.error) {
      setSavedEELinkages([]);
      if (draft) {
        navigate("/dashboard/v2/type-approval/intersection?tab=2");
      } else {
        navigate("/dashboard/v2/type-approval/tickets?tab=2");
      }
    }
  };

  const handleLinkSWMToLRs = async ({ draft }) => {
    const updatedTestCaseIdMap = await updateTestCaseLinksBeforeTicketCreation({
      linkages: savedSWMLinkages,
      entityType: "swm",
    });
    if (!updatedTestCaseIdMap) {
      return;
    }

    let apiDetails = typeApprovalAPI.add_intersection;
    let intersectionDetails = sanitizeLinkagesForTicketCreation(
      updatedTestCaseIdMap,
      savedSWMLinkages,
    );
    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          variant_id: Number(selectedVariantDetails?.variant_id),
          intersections: intersectionDetails,
          is_save_draft: draft,
          current_time: new Date().toISOString(),
        },
        show_toast: true,
      }),
    );
    if (!resp.error) {
      setSavedSWMLinkages([]);
      if (draft) {
        navigate("/dashboard/v2/type-approval/intersection?tab=3");
      } else {
        navigate("/dashboard/v2/type-approval/tickets?tab=3");
      }
    }
  };

  const handleLinkVSToLRs = async ({
    draft,
    linkages,
    apiDetails = typeApprovalAPI?.get_vehicle_add_intersection,
    redirectTab = 0,
    entityType = "vs",
  }) => {
    const updatedTestCaseIdMap = await updateTestCaseLinksBeforeTicketCreation({
      linkages,
      entityType,
    });
    if (!updatedTestCaseIdMap) {
      return;
    }

    let intersectionDetails = sanitizeLinkagesForTicketCreation(
      updatedTestCaseIdMap,
      linkages,
    );
    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          variant_id: Number(selectedVariantDetails?.variant_id),
          intersections: intersectionDetails,
          is_save_draft: draft,
          current_time: new Date().toISOString(),
        },
        show_toast: true,
      }),
    );
    if (!resp.error) {
      setSavedVehicleSystemLinkages([]);
      setSavedVehicleComponentLinkages([]);
      if (draft) {
        navigate(`/dashboard/v2/type-approval/intersection?tab=${redirectTab}`);
      } else {
        navigate(`/dashboard/v2/type-approval/tickets?tab=${redirectTab}`);
      }
    }
  };

  useEffect(() => {
    const nextTab = Number(tabFromURL) || 0;
    setCurrentTabValue(nextTab);
    setTicketComponent(getTicketComponentByTab(nextTab));
    setDisableRaiseTicketButton(true);
  }, [tabFromURL]);
  return (
    <Box>
      <Grid container>
        <Grid item sm={12} sx={{ p: 1 }}>
          <CommonBreadcrumbs
            items={[
              { label: "Regulatory Compliance" },
              { label: "Link & Unlink" },
            ]}
          />
        </Grid>

        <Grid container sx={{ height: "100%", alignItems: "center", p: 2 }}>
          <Grid item sm={12}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box>
                <ProjectVariantSearchbar />
              </Box>

              <Box>
                <ProjectVariantDialog />
              </Box>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <CustomButtonV2
                  btnName="Back"
                  startIcon={<ArrowBackIcon />}
                  handleOnClick={() =>
                    navigate("/dashboard/v2/type-approval/link")
                  }
                />
              </Box>
              {/* FLEX SPACER */}
              <Box sx={{ flexGrow: 1 }} />

              {/* RIGHT-MOST BUTTON */}
              <Box sx={{ pr: 3 }}>
                <CustomButtonV2
                  type="other"
                  size="small"
                  btnName={kgLoading ? "Refreshing…" : "Refresh AI Suggestions"}
                  handleOnClick={handleKgBulkRefresh}
                  disabled={kgLoading}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Grid item sm={12} sx={{ p: 2 }}>
          <CustomTabsV2
            key={currentTabValue} // Force re-render when tab changes
            centerTabs={true}
            defaultTab={currentTabValue}
            onTabChange={(tabIndex) => {
              const tabState = getTabState(tabIndex);
              setCurrentTabValue(tabIndex);
              setTicketComponent(tabState.component);
              setDisableRaiseTicketButton(tabState.linkages.length === 0);
            }}
            renderComponentBelow={
              <Grid item sm={12}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    pr: 3,
                  }}
                >
                  {selectedProjectDetails?.parent_project_id &&
                    selectedVariantDetails?.parent_variant_details
                      ?.variant_id && (
                      <CustomButtonV2
                        btnName="Recreate Ticket"
                        disabled={isLoading}
                        handleOnClick={async () => {
                          let ticketType;
                          switch (ticketComponent) {
                            case "ee":
                              ticketType = 2;
                              break;
                            case "swm":
                              ticketType = 1;
                              break;
                            case "vehicle":
                            case "vehicle-system":
                            case "vehicle-component":
                              ticketType = 3;
                              break;
                            default:
                              break;
                          }

                          setIsLoading(true);
                          await dispatch(
                            HandleApiActions({
                              ...typeApprovalAPI.recreate_parent_tickets,
                              params: {
                                ticket_type: ticketType,
                                project_id: selectedProjectDetails?.project_id,
                                variant_id: selectedVariantDetails?.variant_id,
                                parent_project_id:
                                  selectedProjectDetails?.parent_project_id,
                                parent_variant_id:
                                  selectedVariantDetails?.parent_variant_details
                                    ?.variant_id,
                              },
                              show_toast: true,
                            }),
                          );
                          setIsLoading(false);
                        }}
                      />
                    )}

                  <CustomButtonV2
                    btnName="Save Draft"
                    disabled={disableRaiseTicketButton}
                    handleOnClick={() => {
                      switch (ticketComponent) {
                        case "ee":
                          handleLinkEEToLRs({ draft: true });
                          break;
                        case "swm":
                          handleLinkSWMToLRs({ draft: true });
                          break;
                        case "vehicle":
                        case "vehicle-system":
                          handleLinkVSToLRs({
                            draft: true,
                            linkages: savedVehicleSystemLinkages,
                            apiDetails:
                              typeApprovalAPI?.get_vehicle_systems_add_intersection,
                            redirectTab: 0,
                            entityType: "vs",
                          });
                          break;
                        case "vehicle-component":
                          handleLinkVSToLRs({
                            draft: true,
                            linkages: savedVehicleComponentLinkages,
                            apiDetails:
                              typeApprovalAPI?.get_vehicle_add_intersection,
                            redirectTab: 1,
                            entityType: "vc",
                          });
                          break;
                        default:
                          break;
                      }
                    }}
                    type="blue-outline"
                  />
                  <CustomButtonV2
                    btnName="Raise Ticket"
                    disabled={disableRaiseTicketButton}
                    handleOnClick={() => {
                      switch (ticketComponent) {
                        case "ee":
                          handleLinkEEToLRs({ draft: false });
                          break;
                        case "swm":
                          handleLinkSWMToLRs({ draft: false });
                          break;
                        case "vehicle":
                        case "vehicle-system":
                          handleLinkVSToLRs({
                            draft: false,
                            linkages: savedVehicleSystemLinkages,
                            apiDetails:
                              typeApprovalAPI?.get_vehicle_systems_add_intersection,
                            redirectTab: 0,
                            entityType: "vs",
                          });
                          break;
                        case "vehicle-component":
                          handleLinkVSToLRs({
                            draft: false,
                            linkages: savedVehicleComponentLinkages,
                            apiDetails:
                              typeApprovalAPI?.get_vehicle_add_intersection,
                            redirectTab: 1,
                            entityType: "vc",
                          });
                          break;
                        default:
                          break;
                      }
                    }}
                  />
                </Box>
              </Grid>
            }
            tabs={[
              {
                name: "Vehicle System",
                count: "",
                content: (
                  <RaiseVehicleTickets
                    savedLinkages={savedVehicleSystemLinkages}
                    entityType={"vehicle"}
                    vehicleTicketMode="system"
                    setSavedLinkages={(data) => {
                      setSavedVehicleSystemLinkages(data);
                      setDisableRaiseTicketButton(data.length === 0);
                      setTicketComponent("vehicle-system");
                    }}
                  />
                ),
              },
              {
                name: "Vehicle Component",
                count: "",
                content: (
                  <RaiseVehicleTickets
                    savedLinkages={savedVehicleComponentLinkages}
                    entityType={"vehicle"}
                    vehicleTicketMode="component"
                    setSavedLinkages={(data) => {
                      setSavedVehicleComponentLinkages(data);
                      setDisableRaiseTicketButton(data.length === 0);
                      setTicketComponent("vehicle-component");
                    }}
                  />
                ),
              },
              {
                name: "EE Component",
                count: "",
                content: (
                  <RaiseEETicket
                    savedLinkages={savedEELinkages}
                    entityType={"ee"}
                    onBeforeSmartMatch={handleKgBulkRefresh}
                    setSavedLinkages={(data) => {
                      setSavedEELinkages(data);
                      setDisableRaiseTicketButton(data.length === 0);
                      setTicketComponent("ee");
                    }}
                  />
                ),
              },
              {
                name: "Software Module",
                count: "",
                content: (
                  <RaiseSoftwareTickets
                    savedLinkages={savedSWMLinkages}
                    entityType={"swm"}
                    onBeforeSmartMatch={handleKgBulkRefresh}
                    setSavedLinkages={(data) => {
                      setSavedSWMLinkages(data);
                      setDisableRaiseTicketButton(data.length === 0);
                      setTicketComponent("swm");
                    }}
                  />
                ),
              },
            ]}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default RaiseTicket;
